import TrebbleButton from "../../../../common/TrebbleButton";
import TrebbleModal from "../../../../common/TrebbleModal";
import TrebbleCard from "../../../../common/TrebbleCard";
import TrebbleSwitch from "../../../../common/TrebbleSwitch";
import React, { useState } from "react";
import { VideoCanvas } from "../../../remotion/Components/DesignSystem";
import { Template } from "../../VideoEditor";
import { FocusRegion } from "../../../remotion/Components/VideoSegmentMultiFocus/VideoSegmentMultiFocus";
import VideoFocusRegionSelector from "./VideoFocusRegionSelector";
import sampleVideoData from '../../../remotion/video_data_to_render.json';
import { defaultFocusRegions } from "./DefaultLayouts";
import { AspectRatioIcon16x9, AspectRatioIcon1x1, AspectRatioIcon9x16 } from "./AspectRatioIcons";
import { 
    DefaultLayoutIcon, 
    TwoSplitLayoutIcon, 
    ScreenshareLayoutIcon, 
    GameplayLayoutIcon,
    ThreeSpeakersLayoutIcon,
    FourSpeakersLayoutIcon,
    SquareLayoutIcon,
    PortraitLayoutIcon,
    LandscapeLayoutIcon
} from "./LayoutIcons";
import { IconSelectorCompact } from './IconSelectorCompact';
import { FieldContainer } from '../CaptionConfig/FieldContainer';
import { IconDropdown } from '../LayoutConfig/IconDropdown';
import { SquareBorderIcon, SmoothBorderIcon, RoundedBorderIcon } from './../FrameConfig/BorderRadiusIcons';
import { NoBorderIcon, ShadowIcon, DarkBorderIcon, LightBorderIcon, Border3DLeftIcon, Border3DRightIcon } from './../FrameConfig/BorderStyleIcons';

import ti18n from 'i18n!nls/ClipCreatori18n';

declare global {
    interface Window {
        alertErrorMessage: (error: any) => void;
        getI18n: (i18nData: any, key: string, options?: any) => string;
    }
}

// SSR-safe i18n accessor
const t = (key: string) =>
  (typeof window !== 'undefined' && window.getI18n)
    ? window.getI18n(ti18n, key)
    : ((ti18n as any)[key] ?? key);

const LAYOUT_CONFIG = Object.freeze({
    DEFAULT:   { value: "default",   label: t('LAYOUT_DEFAULT_LABEL') },
    SQUARE:    { value: 'square',    label: t('LAYOUT_SQUARE_LABEL') },
    PORTRAIT:  { value: 'portrait',  label: t('LAYOUT_PORTRAIT_LABEL') },
    LANDSCAPE: { value: 'landscape', label: t('LAYOUT_LANDSCAPE_LABEL') },
    SPLIT:     { value: "2-split",   label: t('LAYOUT_2SPLIT_LABEL') },
    SCREENSHARE:{value: "screenshare",label: t('LAYOUT_SCREENSHARE_LABEL') },
    GAMEPLAY:  { value: "gameplay",  label: t('LAYOUT_GAMEPLAY_LABEL') },
    THREE:     { value: "3-split",   label: t('LAYOUT_THREE_LABEL') },
    FOUR:      { value: "4-split",   label: t('LAYOUT_FOUR_LABEL') },
});

const ASPECT_RATIOS = Object.freeze({
    SQUARE: {
        value: "square",
        label: t('ASPECT_RATIO_1_1_LABEL'),
        suitable_platforms: [t('ASPECT_RATIO_1_1_PLATFORMS')],
    },
    PORTRAIT: {
        value: "portrait",
        label: t('ASPECT_RATIO_9_16_LABEL'),
        suitable_platforms: [t('ASPECT_RATIO_9_16_PLATFORMS')],
    },
    LANDSCAPE: {
        value: "landscape",
        label: t('ASPECT_RATIO_16_9_LABEL'),
        suitable_platforms: [t('ASPECT_RATIO_16_9_PLATFORMS')],
    },
});

const ASPECT_RATIOS_VALUES_TO_RESOLUTIONS = (value: string): VideoCanvas => {
    switch (value) {
        case ASPECT_RATIOS.SQUARE.value:
            return { aspectRatio: "1:1", width: 1080, height: 1080, safeZone: { top: 100, bottom: 100, left: 100, right: 100 } };
        case ASPECT_RATIOS.PORTRAIT.value:
            return { aspectRatio: "9:16", width: 1080, height: 1920, safeZone: { top: 100, bottom: 100, left: 100, right: 100 } };
        case ASPECT_RATIOS.LANDSCAPE.value:
            return { aspectRatio: "16:9", width: 1920, height: 1080, safeZone: { top: 100, bottom: 100, left: 100, right: 100 } };
        default:
            return { aspectRatio: "16:9", width: 1920, height: 1080, safeZone: { top: 100, bottom: 100, left: 100, right: 100 } };
    }
}

const RESOLUTIONS_TO_ASPECT_RATIOS_VALUES = (resolution: VideoCanvas): string => {
    if (resolution.width === 1080 && resolution.height === 1080) {
        return ASPECT_RATIOS.SQUARE.value;
    } else if (resolution.width === 1080 && resolution.height === 1920) {
        return ASPECT_RATIOS.PORTRAIT.value;
    } else if (resolution.width === 1920 && resolution.height === 1080) {
        return ASPECT_RATIOS.LANDSCAPE.value;
    } else {
        return ASPECT_RATIOS.LANDSCAPE.value;
    }
}

const aspectRatioOptions = [
    {
        value: 'landscape',
        label: t('ASPECT_RATIO_16_9_LABEL'),
        icon: <AspectRatioIcon16x9  />,
        description: t('ASPECT_RATIO_16_9_PLATFORMS')
    },
    {
        value: 'square',
        label: t('ASPECT_RATIO_1_1_LABEL'),
        icon: <AspectRatioIcon1x1 />, 
        description: t('ASPECT_RATIO_1_1_PLATFORMS')
    },
    {
        value: 'portrait',
        label: t('ASPECT_RATIO_9_16_LABEL'),
        icon: <AspectRatioIcon9x16 />, 
        description: t('ASPECT_RATIO_9_16_PLATFORMS')
    }
];

const layoutOptions = [
    {
        value: LAYOUT_CONFIG.DEFAULT.value,
        label: LAYOUT_CONFIG.DEFAULT.label,
        icon: <DefaultLayoutIcon />
    },
    {
        value: LAYOUT_CONFIG.SQUARE.value,
        label: LAYOUT_CONFIG.SQUARE.label,
        icon: <SquareLayoutIcon />
    },
    {
        value: LAYOUT_CONFIG.PORTRAIT.value,
        label: LAYOUT_CONFIG.PORTRAIT.label,
        icon: <PortraitLayoutIcon />
    },
    {
        value: LAYOUT_CONFIG.LANDSCAPE.value,
        label: LAYOUT_CONFIG.LANDSCAPE.label,
        icon: <LandscapeLayoutIcon />
    },
    {
        value: LAYOUT_CONFIG.SPLIT.value,
        label: LAYOUT_CONFIG.SPLIT.label,
        icon: <TwoSplitLayoutIcon />
    },
    {
        value: LAYOUT_CONFIG.SCREENSHARE.value,
        label: LAYOUT_CONFIG.SCREENSHARE.label,
        icon: <ScreenshareLayoutIcon />
    },
    {
        value: LAYOUT_CONFIG.GAMEPLAY.value,
        label: LAYOUT_CONFIG.GAMEPLAY.label,
        icon: <GameplayLayoutIcon />
    },
    {
        value: LAYOUT_CONFIG.THREE.value,
        label: LAYOUT_CONFIG.THREE.label,
        icon: <ThreeSpeakersLayoutIcon />
    },
    {
        value: LAYOUT_CONFIG.FOUR.value,
        label: LAYOUT_CONFIG.FOUR.label,
        icon: <FourSpeakersLayoutIcon />
    }
];


const BORDER_STYLES = Object.freeze({
    NO_BORDER: {
        value: "no_border",
        label: "No border",
        icon: <NoBorderIcon />
    },
    SHADOW: {
        value: "shadow",
        label: "Shadow",
        icon: <ShadowIcon />
    },
    DARK_BORDER: {
        value: "dark_border",
        label: "Dark border",
        icon: <DarkBorderIcon />
    },
    LIGHT_BORDER: {
        value: "light_border",
        label: "Light border",
        icon: <LightBorderIcon />
    },
    BORDER_WITH_3D_LEFT: {
        value: "border_with_3d_left",
        label: "3D border left",
        icon: <Border3DLeftIcon />
    },
    BORDER_WITH_3D_RIGHT: {
        value: "border_with_3d_right",
        label: "3D border right",
        icon: <Border3DRightIcon />
    },
});

const BORDER_CORNER_RADIUS = Object.freeze({
    SQUARE: {
        value: "0",
        label: "Square",
        icon: <SquareBorderIcon />
    },
    SMOOTH: {
        value: "30",
        label: "Smooth",
        icon: <SmoothBorderIcon />
    },
    ROUNDED: {
        value: "50",
        label: "Rounded",
        icon: <RoundedBorderIcon />
    },
});

/**
 * Generate smart focus regions based on source video aspect ratio
 * @param layoutName - The selected layout type
 * @param canvasAspectRatio - Target canvas aspect ratio
 * @param sourceVideoAspectRatio - Source video aspect ratio (width/height)
 */
function getSmartFocusRegions(
    layoutName: string,
    canvasAspectRatio: VideoCanvas['aspectRatio'],
    sourceVideoAspectRatio: number | null
): ReturnType<typeof defaultFocusRegions> {
    // If we don't have source video info, use default
    if (sourceVideoAspectRatio === null) {
        return defaultFocusRegions(layoutName as any, canvasAspectRatio);
    }

    const isSourcePortrait = sourceVideoAspectRatio < 1.0;
    const isSourceLandscape = sourceVideoAspectRatio > 1.0;

    // Portrait layout smart defaults
    if (layoutName === 'portrait') {
        const base = defaultFocusRegions(layoutName as any, canvasAspectRatio);

        // If source is already portrait, use full frame to avoid black borders
        if (isSourcePortrait) {
            return {
                ...base,
                [canvasAspectRatio]: {
                    focusRegions: [{
                        x: 0,
                        y: 0,
                        width: 1,
                        height: 1,
                        aspectRatio: "9:16"
                    }]
                }
            };
        }

        // For landscape source, use default narrow crop
        return base;
    }

    // Square layout smart defaults
    if (layoutName === 'square') {
        const base = defaultFocusRegions(layoutName as any, canvasAspectRatio);

        // If source is portrait, crop center square from full width
        if (isSourcePortrait) {
            const squareHeight = sourceVideoAspectRatio; // Proportional height for square
            return {
                ...base,
                [canvasAspectRatio]: {
                    focusRegions: [{
                        x: 0,
                        y: (1 - squareHeight) / 2, // Center vertically
                        width: 1,
                        height: squareHeight,
                        aspectRatio: "1:1"
                    }]
                }
            };
        }

        // For landscape source, crop center square from full height
        if (isSourceLandscape) {
            const squareWidth = 1 / sourceVideoAspectRatio; // Proportional width for square
            return {
                ...base,
                [canvasAspectRatio]: {
                    focusRegions: [{
                        x: (1 - squareWidth) / 2, // Center horizontally
                        y: 0,
                        width: squareWidth,
                        height: 1,
                        aspectRatio: "1:1"
                    }]
                }
            };
        }

        return base;
    }

    // Landscape layout smart defaults
    if (layoutName === 'landscape') {
        const base = defaultFocusRegions(layoutName as any, canvasAspectRatio);

        // If source is portrait, crop center landscape region
        if (isSourcePortrait) {
            const targetAspectRatio = 16 / 9;
            const landscapeHeight = sourceVideoAspectRatio / targetAspectRatio; // normalized height for 16:9 crop
            return {
                ...base,
                [canvasAspectRatio]: {
                    focusRegions: [{
                        x: 0,
                        y: Math.max(0, (1 - landscapeHeight) / 2),
                        width: 1,
                        height: Math.min(1, landscapeHeight),
                        aspectRatio: "16:9"
                    }]
                }
            };
        }

        // For landscape source, use full frame
        return base;
    }

    // For other layouts, use default
    return defaultFocusRegions(layoutName as any, canvasAspectRatio);
}

interface LayoutComponentProps {
    selectedTemplate: Template;
    setSelectedTemplate: (template: Template) => void;
    videoRenderData: typeof sampleVideoData;
    inCloudRenderingEnvironment: boolean;
    useCompactView?: boolean;
}

export const LayoutComponent: React.FC<LayoutComponentProps> = ({
    selectedTemplate,
    setSelectedTemplate,
    videoRenderData,
    inCloudRenderingEnvironment,
    useCompactView = true
}) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentFocusRegionIndex, setCurrentFocusRegionIndex] = useState<number | null>(null);
    const [sourceVideoAspectRatio, setSourceVideoAspectRatio] = useState<number | null>(null);

    // Detect source video dimensions
    React.useEffect(() => {
        if (!videoRenderData || videoRenderData.length === 0) return;

        const videoUrl = inCloudRenderingEnvironment
            ? videoRenderData[0]?.sequencerNodeReference?.audioSegment?.audioUrl
            : videoRenderData[0]?.cachedBlobUrl;

        if (!videoUrl) return;

        // Create temporary video element to detect dimensions
        const video = document.createElement('video');
        video.src = videoUrl;

        const handleLoadedMetadata = () => {
            if (video.videoWidth && video.videoHeight) {
                const aspectRatio = video.videoWidth / video.videoHeight;
                setSourceVideoAspectRatio(aspectRatio);
            }
        };

        const handleError = () => {
            console.error('Failed to load video metadata for aspect ratio detection');
            // Fallback to null, will use default focus regions
            setSourceVideoAspectRatio(null);
        };

        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('error', handleError);
        video.load();

        return () => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('error', handleError);
            video.src = '';
        };
    }, [videoRenderData, inCloudRenderingEnvironment]);

    const onLayoutChange = (value: string) => {
        if (value === 'default' || value === '2-split' || value === '3-split' || value === '4-split' || value === 'screenshare' || value === 'gameplay' || value === 'square' || value === 'portrait' || value === 'landscape') {
            // Use smart focus regions that adapt to source video aspect ratio
            const newFraming = getSmartFocusRegions(
                value,
                selectedTemplate.designSystem.canvas.aspectRatio,
                sourceVideoAspectRatio
            );
            setSelectedTemplate({ ...selectedTemplate, videoConfig: { ...selectedTemplate.videoConfig, framing: newFraming } });
            return
        }
    }

    const handleEditFocusRegion = (index: number) => {
        setCurrentFocusRegionIndex(index);
        setIsModalVisible(true);
    }

    const handleFocusRegionChange = (updatedRegion: FocusRegion<any>) => {
        if (currentFocusRegionIndex !== null) {
            const updatedFocusRegions = [...selectedTemplate.videoConfig.framing[selectedTemplate.designSystem.canvas.aspectRatio].focusRegions];
            updatedFocusRegions[currentFocusRegionIndex] = updatedRegion;

            setSelectedTemplate({
                ...selectedTemplate,
                videoConfig: {
                    ...selectedTemplate.videoConfig,
                    framing: {
                        ...selectedTemplate.videoConfig.framing,
                        [selectedTemplate.designSystem.canvas.aspectRatio]: {
                            ...selectedTemplate.videoConfig.framing[selectedTemplate.designSystem.canvas.aspectRatio],
                            focusRegions: updatedFocusRegions as any,
                        }
                    }
                }
            });
        }
    }

    return (
        <div className="flex flex-col h-full">
            {/* Video Format Settings */}
            <TrebbleCard size="small" title={t('VIDEO_FORMAT_TITLE')} className="mt-2">
                <FieldContainer label={t('ASPECT_RATIO_LABEL')}>
                    <IconDropdown
                        value={RESOLUTIONS_TO_ASPECT_RATIOS_VALUES(selectedTemplate.designSystem.canvas)}
                        className="w-full"
                        onChange={(value) => {
                            setSelectedTemplate({
                                ...selectedTemplate,
                                designSystem: {
                                    ...selectedTemplate.designSystem,
                                    canvas: ASPECT_RATIOS_VALUES_TO_RESOLUTIONS(value as string)
                                }
                            });
                        }}
                        options={aspectRatioOptions}
                    />
                </FieldContainer>
            </TrebbleCard>

            {/* Layout Settings */}
            <TrebbleCard size="small" title={t('LAYOUT_SETTINGS_TITLE')} className="mt-4">
                <FieldContainer label={t('LAYOUT_LABEL')}>
                    <IconDropdown
                        value={selectedTemplate.videoConfig.framing.name}
                        className="w-full"
                        onChange={(value) => onLayoutChange(value as string)}
                        options={layoutOptions}
                    />
                </FieldContainer>
            </TrebbleCard>

            {/* Focus Regions */}
            {selectedTemplate.videoConfig.framing[selectedTemplate.designSystem.canvas.aspectRatio].focusRegions.length > 0 && (
                <TrebbleCard size="small" title={t('FOCUS_REGIONS_TITLE')} className="mt-4">
                    <FieldContainer label={t('CAMERA_FRAME_LABEL')}>
                        <div className="flex flex-col gap-5">
                            {selectedTemplate.videoConfig.framing[selectedTemplate.designSystem.canvas.aspectRatio].focusRegions.map((focusRegion, index) => {
                                return (
                                    <div key={index} className="flex flex-col gap-2">
                                        <div className="flex flex-col">
                                            <div className="text-sm">{t('FOCUS_REGION_LABEL') + ' ' + (index + 1)}</div>
                                            <div className="text-xs opacity-50">{t('ASPECT_RATIO_LABEL') + ': ' + focusRegion.aspectRatio}</div>
                                        </div>
                                        <TrebbleButton onClick={() => handleEditFocusRegion(index)}>{t('EDIT_FOCUS_REGION_BUTTON')}</TrebbleButton>
                                    </div>
                                );
                            })}
                        </div>
                    </FieldContainer>
                </TrebbleCard>
            )}

            {/* Frame Settings */}
            {false && <TrebbleCard size="small" title="Frame Settings" className="mt-4">
                <div className="flex flex-col">
                    <FieldContainer 
                        label="Spacing" 
                        direction="row"
                    >
                        <TrebbleSwitch
                            size="small"
                            checked={selectedTemplate.videoConfig.padding}
                            onChange={(checked: boolean) => setSelectedTemplate({ ...selectedTemplate, videoConfig: { ...selectedTemplate.videoConfig, padding: checked } })}
                        />
                    </FieldContainer>

                    <FieldContainer label="Border radius">
                        <IconDropdown
                            className="w-full"
                            options={Object.values(BORDER_CORNER_RADIUS)}
                            value={selectedTemplate.videoConfig.borderRadius.toString()}
                            onChange={(value) => setSelectedTemplate({ 
                                ...selectedTemplate, 
                                videoConfig: { 
                                    ...selectedTemplate.videoConfig, 
                                    borderRadius: parseInt(value as string) 
                                } 
                            })}
                        />
                    </FieldContainer>

                    <FieldContainer label="Border style">
                        <IconSelectorCompact
                            value={selectedTemplate.videoConfig.borderStyle || BORDER_STYLES.NO_BORDER.value}
                            onChange={(value) => setSelectedTemplate({ 
                                ...selectedTemplate, 
                                videoConfig: { 
                                    ...selectedTemplate.videoConfig, 
                                    borderStyle: value,
                                    border: value !== BORDER_STYLES.NO_BORDER.value
                                } 
                            })}
                            options={Object.values(BORDER_STYLES)}
                            layout="grid"
                            gridCols={2}
                        />
                    </FieldContainer>
                </div>
            </TrebbleCard>}

            {currentFocusRegionIndex !== null && (
                <TrebbleModal
                    centered
                    title={`Edit Focus Region ${currentFocusRegionIndex + 1}`}
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    footer={null}
                    width={800}
                    destroyOnClose
                >
                    <VideoFocusRegionSelector
                        displayed={isModalVisible}
                        useCard={false}
                        videoUrl={inCloudRenderingEnvironment? videoRenderData[0]?.sequencerNodeReference?.audioSegment?.audioUrl: videoRenderData[0].cachedBlobUrl}
                        focusRegion={selectedTemplate.videoConfig.framing[selectedTemplate.designSystem.canvas.aspectRatio].focusRegions[currentFocusRegionIndex]}
                        onFocusRegionChange={handleFocusRegionChange}
                        instructionLabel={t('FOCUS_REGION_INSTRUCTION')}
                        resetLabel={t('FOCUS_REGION_RESET')}
                    />
                </TrebbleModal>
            )}
        </div>
    );
};